import { useState } from "react";

import { Grid, Typography } from "@mui/material";

import { ToggleButtons } from "@aclymatepackages/atoms";
import { editObjectData } from "@aclymatepackages/array-immutability-helpers";
import { numbersRegExpTest } from "@aclymatepackages/reg-exp";
import { isStateInNortheastCorridor } from "@aclymatepackages/other-helpers";
import { trainTransportationCarbon } from "@aclymatepackages/calcs/travel";

import DistanceInputOptions from "../DistanceInputOptions";

import {
  buildInitialTransactionInputValueFromSchema,
  findDistanceMiles,
} from "../../../helpers/components/inputs";

const TrainInput = ({
  emissionData,
  editEmissionData,
  setCalculatedMileage,
  setCalcLoading,
  rideMileageEstimate,
  trainType,
  setTrainType,
  disabled,
}) => {
  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <DistanceInputOptions
          type="Train"
          inputData={emissionData}
          editInputData={editEmissionData}
          setCalculatedMileage={setCalculatedMileage}
          setCalcLoading={setCalcLoading}
          oldDbMileageValue={rideMileageEstimate}
          disabled={disabled}
        />
      </Grid>
      <Grid item>
        <Typography variant="h6" gutterBottom>
          What type of train are you riding?
        </Typography>
        <ToggleButtons
          buttons={[
            { name: "Commuter Rail", value: "commuterRail" },
            { name: "Metro Rail", value: "metroRail" },
            { name: "Amtrak", value: "intercityRail" },
          ]}
          value={trainType}
          onChange={setTrainType}
          disabled={disabled}
        />
      </Grid>
    </Grid>
  );
};

const useTrainInput = ({ transaction, onSave, setCalcLoading }) => {
  const { status } = transaction;
  const inputSchema = [
    { field: "rideFrom" },
    { field: "from" }, //Same as RidesInputBlock for why we need this
    { field: "rideTo" },
    { field: "to" },
    { field: "rideMileageEstimate" },
    { field: "distance" },
    { field: "distanceUnit" },
    { field: "trainType" },
  ];

  const [emissionData, setEmissionData] = useState(
    buildInitialTransactionInputValueFromSchema(transaction, inputSchema)
  );
  const [calculatedMileage, setCalculatedMileage] = useState(0);

  const {
    from,
    rideFrom,
    to,
    rideTo,
    rideMileageEstimate,
    distance,
    distanceUnit,
    trainType,
    roundTrip,
  } = emissionData;

  const editEmissionData = (field) => (value) =>
    editObjectData(setEmissionData, field, value);

  const isRideToAndRideFromInNortheastCorridor = () => {
    if (to && from) {
      const { state: rideToState } = to || {};
      const { state: rideFromState } = from || {};

      return (
        isStateInNortheastCorridor(rideToState) &&
        isStateInNortheastCorridor(rideFromState)
      );
    }

    return false;
  };

  const findRoundTripDistance = () => {
    const distanceMiles =
      findDistanceMiles(emissionData, calculatedMileage) || rideMileageEstimate;

    if (from && to && roundTrip) {
      return distanceMiles * 2;
    }

    return distanceMiles;
  };

  const tonsCo2e = trainTransportationCarbon(
    trainType,
    findRoundTripDistance(),
    isRideToAndRideFromInNortheastCorridor()
  );

  const descriptionObj =
    rideTo && rideFrom
      ? { description: `${rideFrom.description} -> ${rideTo.description}` }
      : {};

  const onTransactionSave = () =>
    onSave({
      tonsCo2e,
      emissionData: {
        ...descriptionObj,
        ...emissionData,
      },
    });

  const saveEnabled =
    (rideTo && rideFrom && trainType) ||
    (!!distance && numbersRegExpTest(distance) && trainType && distanceUnit) ||
    tonsCo2e;

  return {
    inputBlock: (
      <TrainInput
        emissionData={{
          ...emissionData,
          from: from || rideFrom,
          to: to || rideTo,
        }}
        editEmissionData={editEmissionData}
        setCalculatedMileage={setCalculatedMileage}
        setCalcLoading={setCalcLoading}
        rideMileageEstimate={rideMileageEstimate}
        trainType={trainType}
        setTrainType={editEmissionData("trainType")}
        disabled={status === "locked"}
      />
    ),
    saveEnabled,
    onTransactionSave,
    tonsCo2e,
  };
};

export default useTrainInput;
